import React, { useCallback } from 'react';

import { Control, Controller, FieldErrors, UseFormRegister, useFieldArray } from 'react-hook-form';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import DateWidget from '~/components/Common/Elements/DateWidget';

import { FlightLegsFormValues } from './CustomOfferFlightLegsSection';

type FlightLegSegmentsProps = {
  legIndex: number;
  control: Control<FlightLegsFormValues>;
  register: UseFormRegister<FlightLegsFormValues>;
  errors: FieldErrors<FlightLegsFormValues>;
  isSubmitting: boolean;
  isBusinessTraveller: boolean;
};

export function FlightLegSegments({
  legIndex,
  control,
  register,
  errors,
  isSubmitting,
  isBusinessTraveller,
}: FlightLegSegmentsProps) {
  const {
    fields: segments,
    append: addSegment,
    remove: removeSegment,
  } = useFieldArray({
    control,
    name: `item.flight_legs.${legIndex}.segments`,
  });

  const handleAddSegment = useCallback(() => {
    const newSegment: App.CustomOfferFlightSegment = {
      airline: '',
      flightNumber: '',
      bookingClass: '',
      departureDate: '',
      departureAirport: '',
      departureAirportName: '',
      arrivalAirport: '',
      arrivalAirportName: '',
      departureTime: '',
      arrivalTime: '',
      arrivalDate: '',
      airlinePnr: '',
    };
    addSegment(newSegment);
  }, [addSegment]);

  return (
    <>
      {segments.map((segment, segmentIndex) => (
        <Stack
          key={segment.id}
          direction="column"
          gap={2}
          padding={2}
          sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, mb: 2, bgcolor: 'background.paper' }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight="medium" color="text.secondary">
              Segment {segmentIndex + 1}
              {segmentIndex > 0 && ' (Connecting Flight)'}
            </Typography>
            {segments.length > 1 && !isSubmitting && (
              <Button
                variant="text"
                size="small"
                color="error"
                title="Remove segment"
                data-testid={`remove-segment-btn-${legIndex}-${segmentIndex}`}
                onClick={() => removeSegment(segmentIndex)}
                startIcon={<DeleteIcon />}
              >
                Remove Segment
              </Button>
            )}
          </Stack>

          <Grid container columns={12} spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.airline`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.airline?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.airline}
                disabled={isSubmitting}
                label="Airline"
                required={isBusinessTraveller}
                placeholder="e.g., EK"
                fullWidth
                data-testid={`segment-airline-${legIndex}-${segmentIndex}`}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.flightNumber`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.flightNumber?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.flightNumber}
                disabled={isSubmitting}
                label="Flight Number"
                placeholder="e.g., 405"
                fullWidth
                data-testid={`segment-flight-number-${legIndex}-${segmentIndex}`}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.bookingClass`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.bookingClass?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.bookingClass}
                disabled={isSubmitting}
                label="Booking class"
                placeholder="e.g., Y"
                fullWidth
                data-testid={`segment-booking-class-${legIndex}-${segmentIndex}`}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.airlinePnr`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.airlinePnr?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.airlinePnr}
                disabled={isSubmitting}
                label="Airline PNR"
                placeholder="e.g., MF6XWW"
                fullWidth
                data-testid={`segment-airline-pnr-${legIndex}-${segmentIndex}`}
              />
            </Grid>
          </Grid>

          <Grid container columns={12} spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.departureAirportName`)}
                helperText={
                  errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.departureAirportName?.message
                }
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.departureAirportName}
                disabled={isSubmitting}
                label="Departure City"
                placeholder="e.g., Melbourne"
                fullWidth
                data-testid={`segment-departure-airport-name-${legIndex}-${segmentIndex}`}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.departureAirport`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.departureAirport?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.departureAirport}
                disabled={isSubmitting}
                label="Departure Airport"
                required
                placeholder="e.g., MEL"
                fullWidth
                data-testid={`segment-departure-airport-${legIndex}-${segmentIndex}`}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Controller
                name={`item.flight_legs.${legIndex}.segments.${segmentIndex}.departureDate`}
                control={control}
                render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                  <DateWidget
                    {...field}
                    label="Departure Date"
                    slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                    disabled={isSubmitting}
                    fullWidth
                    data-testid={`segment-departure-date-${legIndex}-${segmentIndex}`}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.departureTime`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.departureTime?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.departureTime}
                disabled={isSubmitting}
                label="Departure Time"
                placeholder="HH:MM e.g., 23:55"
                fullWidth
                data-testid={`segment-departure-time-${legIndex}-${segmentIndex}`}
              />
            </Grid>
          </Grid>

          <Grid container columns={12} spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.arrivalAirportName`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.arrivalAirportName?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.arrivalAirportName}
                disabled={isSubmitting}
                label="Arrival City"
                placeholder="e.g., Dubai"
                fullWidth
                data-testid={`segment-arrival-airport-name-${legIndex}-${segmentIndex}`}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.arrivalAirport`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.arrivalAirport?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.arrivalAirport}
                disabled={isSubmitting}
                label="Arrival Airport"
                required
                placeholder="e.g., DXB"
                fullWidth
                data-testid={`segment-arrival-airport-${legIndex}-${segmentIndex}`}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Controller
                name={`item.flight_legs.${legIndex}.segments.${segmentIndex}.arrivalDate`}
                control={control}
                render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                  <DateWidget
                    {...field}
                    label="Arrival Date"
                    slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                    disabled={isSubmitting}
                    fullWidth
                    data-testid={`segment-arrival-date-${legIndex}-${segmentIndex}`}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                {...register(`item.flight_legs.${legIndex}.segments.${segmentIndex}.arrivalTime`)}
                helperText={errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.arrivalTime?.message}
                error={!!errors.item?.flight_legs?.[legIndex]?.segments?.[segmentIndex]?.arrivalTime}
                disabled={isSubmitting}
                label="Arrival Time"
                placeholder="HH:MM e.g., 07:00"
                fullWidth
                data-testid={`segment-arrival-time-${legIndex}-${segmentIndex}`}
              />
            </Grid>
          </Grid>
        </Stack>
      ))}

      {!isSubmitting && (
        <Button
          color="primary"
          title="Add connecting flight segment"
          data-testid={`add-segment-btn-${legIndex}`}
          variant="text"
          size="small"
          onClick={handleAddSegment}
          startIcon={<AddIcon />}
          sx={{ mt: 1 }}
        >
          Add Connecting Flight
        </Button>
      )}
    </>
  );
}
